<template>
  <div class="majors-list-page">
    <!-- 搜索栏 -->
    <div class="search-header">
      <div class="search-box">
        <!-- 第一行：选项卡 + 志愿查询 -->
        <div class="search-row search-row-first">
          <div class="tab-switch">
            <button 
              class="tab-btn" 
              :class="{ active: currentTab === 'major' }"
              @click="currentTab = 'major'"
            >专业库</button>
            <button 
              class="tab-btn" 
              :class="{ active: currentTab === 'school' }"
              @click="currentTab = 'school'"
            >院校库</button>
          </div>
          <button class="search-btn home-btn" @click="goToHomePage" style="background-color: #67C23A;">首页</button>
        </div>
        <!-- 第二行：搜索输入框 + 按钮 -->
        <div class="search-row search-row-second">
          <input 
            v-if="currentTab === 'major'"
            v-model="searchKeyword" 
            type="text" 
            placeholder="搜索学科门类/专业类/专业名称..." 
            @keyup.enter="searchMajor"
          />
          <input 
            v-else
            v-model="schoolSearchKeyword" 
            type="text" 
            placeholder="搜索学校名称..." 
            @keyup.enter="searchSchool"
          />
          <button class="search-btn" @click="currentTab === 'major' ? searchMajor() : searchSchool()">搜索</button>
          <button v-if="searchKeyword || schoolSearchKeyword" class="clear-btn" @click="clearSearch">清除</button>
        </div>
      </div>
    </div>
    
    <!-- 访问限制提示 -->
    <div v-if="!canAccess" class="access-limit-warning">
      <div class="warning-content">
        <div class="warning-icon">⚠️</div>
        <div class="warning-text">
          <h3>访问受限</h3>
          <p>{{ accessMessage }}</p>
          <p v-if="accessLimitConfig" class="limit-period">
            非开发时间段：{{ accessLimitConfig.display_period }}
          </p>
        </div>
      </div>
    </div>

    <!-- 专业库内容 -->
    <template v-if="currentTab === 'major' && canAccess">
    <!-- 顶部学科门类选择 -->
    <div class="discipline-header">
      <div 
        v-for="discipline in disciplines" 
        :key="discipline.name"
        class="discipline-item"
        :class="{ active: currentDiscipline === discipline.name }"
        @click="selectDiscipline(discipline.name)"
      >
        <div class="discipline-icon" :style="{ backgroundColor: discipline.color + '20', color: discipline.color }">
          {{ discipline.icon }}
        </div>
        <div class="discipline-name">{{ discipline.name }}</div>
      </div>
    </div>

    <!-- 主体内容区 -->
    <div class="main-content">
      <!-- 左侧专业类列表 -->
      <div class="category-sidebar">
        <div 
          v-for="(majors, category) in currentCategories" 
          :key="category"
          class="category-item"
          :class="{ active: currentCategory === category }"
          @click="selectCategory(category)"
        >
          <span class="category-hot" v-if="isHotCategory(category)">🔥</span>
          {{ category }}
        </div>
      </div>

      <!-- 右侧专业列表 -->
      <div class="majors-content" ref="majorsContent">
        <div v-for="(majors, category) in currentCategories" :key="category" :id="'category-' + category" class="category-section">
          <div class="category-title">
            {{ category }} 
            <span class="category-count">({{ majors.length }}个)</span>
          </div>
          
          <div class="majors-list">
            <div 
              v-for="(major, index) in majors" 
              :key="index"
              class="major-item"
              :class="{ highlight: searchResult === major.major_name }"
            >
              <el-popover
                trigger="hover"
                placement="top"
                :open-delay="200"
                :disabled="!major.grad_dir && !major.main_sub && !major.job_dir"
                popper-class="major-graddir-popover"
              >
                <div v-if="major.main_sub" class="graddir-label">专业课程</div>
                <div v-if="major.main_sub" class="graddir-content">{{ major.main_sub }}</div>
                <div v-if="major.main_sub && major.grad_dir" class="graddir-divider"></div>
                <div v-if="major.grad_dir" class="graddir-label">考研方向</div>
                <div v-if="major.grad_dir" class="graddir-content">{{ major.grad_dir }}</div>
                <div v-if="(major.grad_dir || major.main_sub) && major.job_dir" class="graddir-divider"></div>
                <div v-if="major.job_dir" class="graddir-label">就业方向</div>
                <div v-if="major.job_dir" class="graddir-content">{{ major.job_dir }}</div>
                <span slot="reference" class="major-name" :class="{ 'major-name-hoverable': major.grad_dir || major.main_sub || major.job_dir }">{{ major.major_name }}</span>
              </el-popover>
              <div class="major-tags">
                <span class="tag duration">{{ major.duration }}</span>
                <span class="tag degree">{{ major.degree_type }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </template>

    <!-- 院校库内容 -->
    <template v-else-if="canAccess">
      <!-- 院校筛选条件 -->
      <div class="school-filter">
        <div class="filter-row">
          <div class="filter-item">
            <label>省份</label>
            <select v-model="schoolFilter.province" @change="filterSchools">
              <option value="">全部</option>
              <option v-for="prov in provinces" :key="prov" :value="prov">{{ prov }}</option>
            </select>
          </div>
          <div class="filter-item">
            <label>类型</label>
            <select v-model="schoolFilter.type" @change="filterSchools">
              <option value="">全部</option>
              <option value="综合">综合</option>
              <option value="理工">理工</option>
              <option value="师范">师范</option>
              <option value="医药">医药</option>
              <option value="财经">财经</option>
              <option value="农林">农林</option>
              <option value="语言">语言</option>
              <option value="政法">政法</option>
              <option value="民族">民族</option>
              <option value="艺术">艺术</option>
              <option value="体育">体育</option>
              <option value="军事">军事</option>
              <option value="其他">其他</option>
            </select>
          </div>
          <div class="filter-item">
            <label>层次</label>
            <select v-model="schoolFilter.level" @change="filterSchools">
              <option value="">全部</option>
              <option value="985">985</option>
              <option value="211">211</option>
              <option value="双一流">双一流</option>
              <option value="普通本科">普通本科</option>
              <option value="高职专科">高职专科</option>
            </select>
          </div>
        </div>
      </div>

      <!-- 院校列表 -->
      <div class="school-list-container">
        <div class="school-list">
          <div v-for="school in schools" :key="school.name" class="school-item" @click="viewSchoolMajors(school)">
            <div class="school-header">
              <div class="school-name">
                <span v-if="school.ranking" class="rank-badge">{{ school.ranking }}</span>
                {{ school.name }}
              </div>
              <div class="school-tags">
                <span v-if="school.is_985" class="tag tag-985">985</span>
                <span v-if="school.is_211" class="tag tag-211">211</span>
                <span v-if="school.is_double_first" class="tag tag-double">双一流</span>
                <span class="tag tag-type">{{ school.type }}</span>
              </div>
            </div>
            <div class="school-info">
              <span class="info-item">
                <svg class="location-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="#ff4d4f"/>
                </svg>
                {{ school.province }}
              </span>
              <span class="info-item" v-if="school.website"><i class="icon">🌐</i>{{ school.website }}</span>
            </div>
          </div>
        </div>
        <div v-if="schools.length === 0" class="empty-tip">
          暂无符合条件的院校
        </div>
        
        <!-- 分页 -->
        <div v-if="schoolTotal > 0" class="pagination">
          <div class="pagination-info">
            共 {{ schoolTotal }} 所院校，第 {{ schoolPage }} / {{ schoolTotalPages }} 页
          </div>
          <div class="pagination-buttons">
            <button 
              class="page-btn" 
              :disabled="schoolPage === 1"
              @click="changeSchoolPage(schoolPage - 1)"
            >上一页</button>
            <button 
              v-for="page in displayedPages" 
              :key="page"
              class="page-btn"
              :class="{ active: page === schoolPage }"
              @click="changeSchoolPage(page)"
            >{{ page }}</button>
            <button 
              class="page-btn" 
              :disabled="schoolPage === schoolTotalPages"
              @click="changeSchoolPage(schoolPage + 1)"
            >下一页</button>
          </div>
          <div class="page-size-selector">
            <span>每页</span>
            <select v-model="schoolPageSize" @change="handlePageSizeChange">
              <option :value="10">10</option>
              <option :value="20">20</option>
              <option :value="50">50</option>
            </select>
            <span>条</span>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'MajorsList',
  data() {
    return {
      // 选项卡
      currentTab: 'major', // 'major' 或 'school'
      // 专业库数据
      disciplines: [],
      majorsData: {},
      currentDiscipline: '',
      currentCategory: '',
      hotCategories: ['热门', '计算机类', '电子信息类', '电气类'],
      searchKeyword: '',
      searchResult: null,
      // 院校库数据
      schoolSearchKeyword: '',
      provinces: ['北京', '天津', '河北', '山西', '内蒙古', '辽宁', '吉林', '黑龙江', '上海', '江苏', '浙江', '安徽', '福建', '江西', '山东', '河南', '湖北', '湖南', '广东', '广西', '海南', '重庆', '四川', '贵州', '云南', '西藏', '陕西', '甘肃', '青海', '宁夏', '新疆', '台湾', '香港', '澳门'],
      schoolFilter: {
        province: '',
        type: '',
        level: ''
      },
      schools: [],
      schoolPage: 1,
      schoolPageSize: 20,
      schoolTotal: 0,
      schoolTotalPages: 0,
      // 访问权限控制
      canAccess: true,
      accessMessage: '',
      accessLimitConfig: null
    }
  },
  computed: {
    currentCategories() {
      return this.majorsData[this.currentDiscipline] || {}
    },
    // 分页显示的页码
    displayedPages() {
      const pages = []
      const maxDisplay = 5
      let start = Math.max(1, this.schoolPage - Math.floor(maxDisplay / 2))
      let end = Math.min(this.schoolTotalPages, start + maxDisplay - 1)
      
      if (end - start < maxDisplay - 1) {
        start = Math.max(1, end - maxDisplay + 1)
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i)
      }
      return pages
    }
  },
  mounted() {
    this.checkAccessPermission()
  },
  methods: {
    // 检查访问权限
    async checkAccessPermission() {
      try {
        const response = await axios.get('/api/check-majors-access/')
        if (response.data.success) {
          this.canAccess = response.data.can_access
          this.accessMessage = response.data.message
          this.accessLimitConfig = response.data.limit_config
          
          if (this.canAccess) {
            // 有权限，根据当前选项卡加载对应数据
            if (this.currentTab === 'major') {
              this.fetchData()
            } else {
              this.fetchSchools()
            }
          } else {
            // 无权限，显示限制提示
            this.$message.warning(this.accessMessage)
          }
        } else {
          // API 返回失败，默认允许访问
          this.canAccess = true
          // 根据当前选项卡加载对应数据
          if (this.currentTab === 'major') {
            this.fetchData()
          } else {
            this.fetchSchools()
          }
        }
      } catch (error) {
        console.error('检查访问权限失败:', error)
        // 出错时默认允许访问
        this.canAccess = true
        // 根据当前选项卡加载对应数据
        if (this.currentTab === 'major') {
          this.fetchData()
        } else {
          this.fetchSchools()
        }
      }
    },
    async fetchData() {
      try {
        // 获取学科门类
        const disciplinesRes = await axios.get('/api/disciplines/')
        if (disciplinesRes.data.success) {
          this.disciplines = disciplinesRes.data.data
        }
        
        // 获取专业列表
        const majorsRes = await axios.get('/api/majors-list/')
        if (majorsRes.data.success) {
          this.majorsData = majorsRes.data.data
          
          // 设置默认选中的学科门类
          const disciplineNames = Object.keys(this.majorsData)
          if (disciplineNames.length > 0) {
            // 优先选择"工学"，如果不存在则选择第一个
            this.currentDiscipline = disciplineNames.includes('工学') ? '工学' : disciplineNames[0]
            // 设置默认选中的专业类为第一个
            const categories = Object.keys(this.majorsData[this.currentDiscipline])
            if (categories.length > 0) {
              this.currentCategory = categories[0]
            }
          } else if (this.disciplines.length > 0) {
            this.currentDiscipline = this.disciplines[0].name
          }
        }
      } catch (error) {
        console.error('获取专业列表失败:', error)
        this.$message.error('获取专业列表失败')
      }
    },
    selectDiscipline(name) {
      this.currentDiscipline = name
      // 切换到该学科门类下的第一个专业类
      this.$nextTick(() => {
        const categories = Object.keys(this.currentCategories)
        if (categories.length > 0) {
          this.currentCategory = categories[0]
          // 滚动到顶部
          const contentEl = this.$refs.majorsContent
          if (contentEl) {
            contentEl.scrollTop = 0
          }
        }
      })
    },
    selectCategory(category) {
      this.currentCategory = category
      // 滚动到对应分类区域
      this.$nextTick(() => {
        const element = document.getElementById('category-' + category)
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      })
    },
    isHotCategory(category) {
      return this.hotCategories.includes(category)
    },
    searchMajor() {
      if (!this.searchKeyword.trim()) {
        this.$message.warning('请输入搜索关键词')
        return
      }
      
      const keyword = this.searchKeyword.trim().toLowerCase()
      let found = false
      
      // 1. 先搜索学科门类
      for (const discipline in this.majorsData) {
        if (discipline.toLowerCase().includes(keyword)) {
          // 找到匹配的学科门类
          this.currentDiscipline = discipline
          const categories = Object.keys(this.majorsData[discipline])
          if (categories.length > 0) {
            this.currentCategory = categories[0]
            found = true
            
            // 滚动到第一个分类
            this.$nextTick(() => {
              const categoryEl = document.getElementById('category-' + categories[0])
              if (categoryEl) {
                categoryEl.scrollIntoView({ behavior: 'smooth', block: 'start' })
              }
            })
          }
          return
        }
      }
      
      // 2. 再搜索专业类
      for (const discipline in this.majorsData) {
        const categories = this.majorsData[discipline]
        for (const category in categories) {
          if (category.toLowerCase().includes(keyword)) {
            // 找到匹配的专业类
            this.currentDiscipline = discipline
            this.currentCategory = category
            this.searchResult = null
            found = true
            
            // 滚动到对应分类
            this.$nextTick(() => {
              const categoryEl = document.getElementById('category-' + category)
              if (categoryEl) {
                categoryEl.scrollIntoView({ behavior: 'smooth', block: 'start' })
              }
            })
            
            return
          }
        }
      }
      
      // 3. 最后搜索专业名称
      for (const discipline in this.majorsData) {
        const categories = this.majorsData[discipline]
        for (const category in categories) {
          const majors = categories[category]
          for (const major of majors) {
            if (major.major_name && major.major_name.toLowerCase().includes(keyword)) {
              // 找到匹配的专业
              this.currentDiscipline = discipline
              this.currentCategory = category
              this.searchResult = major.major_name
              found = true
              
              // 滚动到对应分类并高亮
              this.$nextTick(() => {
                const categoryEl = document.getElementById('category-' + category)
                if (categoryEl) {
                  categoryEl.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }
              })
              
              return
            }
          }
        }
      }
      
      if (!found) {
        this.$message.warning('未找到匹配的内容')
      }
    },
    clearSearch() {
      this.searchKeyword = ''
      this.searchResult = null
      this.schoolSearchKeyword = ''
    },
    goToHomePage() {
      this.$router.push('/')
    },
    // 查看院校所有专业
    viewSchoolMajors(school) {
      // 使用URL参数传递学校名（更可靠，兼容微信浏览器）
      // 注意：项目使用hash模式路由，参数格式为 /#/?school=xxx
      window.location.href = '/#/?school=' + encodeURIComponent(school.name)
    },
    // 院校库方法
    async fetchSchools() {
      try {
        // 构建请求参数
        const params = {
          page: this.schoolPage,
          page_size: this.schoolPageSize,
          province: this.schoolFilter.province,
          type: this.schoolFilter.type,
          level: this.schoolFilter.level,
          keyword: this.schoolSearchKeyword
        }
        
        // 从后端 API 获取院校数据
        const response = await axios.get('/api/universities/', { 
          params,
          withCredentials: true 
        })
        if (response.data.success) {
          this.schools = response.data.data || []
          this.schoolTotal = response.data.count || 0
          this.schoolTotalPages = response.data.total_pages || 1
        } else {
          this.$message.error(response.data.message || '获取院校列表失败')
        }
      } catch (error) {
        console.error('获取院校列表失败:', error)
        this.$message.error('获取院校列表失败')
      }
    },
    // 筛选条件变化时重置页码并获取数据
    filterSchools() {
      this.schoolPage = 1
      this.fetchSchools()
    },
    // 搜索学校
    searchSchool() {
      this.schoolPage = 1
      this.fetchSchools()
    },
    // 切换页码
    changeSchoolPage(page) {
      if (page < 1 || page > this.schoolTotalPages) return
      this.schoolPage = page
      this.fetchSchools()
      // 滚动到顶部
      const container = document.querySelector('.school-list-container')
      if (container) {
        container.scrollTop = 0
      }
    },
    // 每页条数变化
    handlePageSizeChange() {
      this.schoolPage = 1
      this.fetchSchools()
    }
  },
  watch: {
    currentTab(newVal) {
      if (newVal === 'school' && this.schools.length === 0) {
        this.fetchSchools()
      }
    }
  }
}
</script>

<style scoped>
.majors-list-page {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #f5f5f5;
}

/* 搜索栏 */
.search-header {
  padding: 10px 15px;
  background-color: #fff;
  border-bottom: 1px solid #e0e0e0;
}

.search-box {
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-width: 100%;
  margin: 0 auto;
}

.search-row {
  display: flex;
  gap: 10px;
  align-items: center;
  width: 100%;
}

.search-row-first {
  justify-content: space-between;
}

.search-row-second {
  flex-wrap: wrap;
}

.search-row-second input {
  flex: 1;
  min-width: 200px;
}

.search-row input {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  font-size: 14px;
  min-width: 150px;
}

.search-row input:focus {
  outline: none;
  border-color: #409EFF;
}

.search-btn {
  padding: 8px 20px;
  background-color: #409EFF;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  white-space: nowrap;
  flex-shrink: 0;
}

.search-btn:hover {
  background-color: #66b1ff;
}

.search-btn {
  transition: background-color 0.3s ease;
}

.clear-btn {
  padding: 8px 15px;
  background-color: #909399;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  white-space: nowrap;
  flex-shrink: 0;
}

.clear-btn:hover {
  background-color: #a6a9ad;
}

.home-btn {
  padding: 6px 12px;
  font-size: 13px;
  flex: 0 0 auto;
  width: auto;
  transition: background-color 0.3s ease;
}

/* 首页按钮悬停效果 - 只变色不加阴影 */
.majors-list-page .search-btn.home-btn:hover {
  background-color: #85ce61 !important;
}

/* 顶部学科门类 */
.discipline-header {
  display: flex;
  justify-content: space-around;
  padding: 15px 10px;
  background-color: #fff;
  border-bottom: 1px solid #e0e0e0;
  overflow-x: auto;
}

.discipline-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  padding: 5px 10px;
  min-width: 60px;
  flex-shrink: 0;
}

.discipline-item.active .discipline-icon {
  border: 2px solid #409EFF;
}

.discipline-item.active .discipline-name {
  color: #409EFF;
  font-weight: bold;
}

.discipline-icon {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  margin-bottom: 5px;
}

.discipline-name {
  font-size: 12px;
  color: #333;
  white-space: nowrap;
  flex-shrink: 0;
}

/* 主体内容 */
.main-content {
  flex: 1;
  display: flex;
  flex-direction: row;
  overflow: hidden;
  width: 100%;
}

/* 左侧专业类 */
.category-sidebar {
  width: 120px;
  min-width: 120px;
  background-color: #fff;
  overflow-y: auto;
  border-right: 1px solid #e0e0e0;
  height: 100%;
}

.category-item {
  padding: 20px 12px;
  font-size: 14px;
  color: #666;
  cursor: pointer;
  border-left: 3px solid transparent;
  display: flex;
  align-items: center;
  gap: 5px;
  min-height: 60px;
}

.category-item.active {
  background-color: #f0f9ff;
  color: #409EFF;
  border-left-color: #409EFF;
  font-weight: bold;
}

.category-hot {
  font-size: 12px;
}

/* 右侧专业列表 */
.majors-content {
  flex: 1;
  padding: 15px;
  padding-right: 20px;
  overflow-y: auto;
  background-color: #fff;
  height: 100%;
  min-width: 0;
}

.category-section {
  margin-bottom: 30px;
}

.category-section:last-child {
  margin-bottom: 0;
}

.category-title {
  font-size: 16px;
  font-weight: bold;
  color: #333;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e0e0e0;
}

.category-count {
  font-size: 14px;
  color: #999;
  font-weight: normal;
}

.majors-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 15px;
}

.major-item {
  padding: 12px;
  background-color: #fafafa;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
}

.major-name {
  font-size: 15px;
  color: #333;
  font-weight: 500;
  margin-bottom: 8px;
}

.major-name-hoverable {
  cursor: pointer;
  color: #409EFF;
  transition: color 0.2s;
}

.major-name-hoverable:hover {
  color: #337ecc;
}

/* 考研方向弹窗内容 */
.graddir-label {
  font-weight: 600;
  color: #409EFF;
  margin-bottom: 6px;
  font-size: 13px;
}

.graddir-content {
  font-size: 13px;
  color: #333;
  line-height: 1.6;
  max-width: 280px;
  white-space: pre-wrap;
  word-break: break-all;
}

.graddir-divider {
  height: 1px;
  background: #eee;
  margin: 8px 0;
}

.major-tags {
  display: flex;
  gap: 8px;
}

.tag {
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
}

.tag.duration {
  background-color: #e6f7ff;
  color: #1890ff;
  border: 1px solid #91d5ff;
}

.tag.degree {
  background-color: #f6ffed;
  color: #52c41a;
  border: 1px solid #b7eb8f;
}

/* 搜索结果高亮 */
.major-item.highlight {
  background-color: #fff3cd;
  border-color: #ffc107;
  box-shadow: 0 0 10px rgba(255, 193, 7, 0.5);
}

/* 选项卡切换 */
.tab-switch {
  display: flex;
  gap: 0;
  background-color: #f0f0f0;
  border-radius: 4px;
  padding: 2px;
}

.tab-btn {
  padding: 6px 16px;
  border: none;
  background-color: transparent;
  color: #666;
  cursor: pointer;
  font-size: 14px;
  border-radius: 3px;
  transition: all 0.3s;
  white-space: nowrap;
  flex-shrink: 0;
}

.tab-btn.active {
  background-color: #fff;
  color: #409EFF;
  font-weight: 500;
  box-shadow: 0 1px 4px rgba(0,0,0,0.1);
}

/* 院校库筛选 */
.school-filter {
  padding: 15px;
  background-color: #fff;
  border-bottom: 1px solid #e0e0e0;
}

.filter-row {
  display: flex;
  gap: 20px;
  max-width: 800px;
  margin: 0 auto;
}

.filter-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter-item label {
  font-size: 14px;
  color: #666;
  white-space: nowrap;
}

.filter-item select {
  padding: 6px 12px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  font-size: 14px;
  min-width: 120px;
}

.filter-item select:focus {
  outline: none;
  border-color: #409EFF;
}

/* 院校列表 */
.school-list-container {
  flex: 1;
  overflow-y: auto;
  padding: 15px;
  background-color: #f5f5f5;
}

.school-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 15px;
  max-width: 1200px;
  margin: 0 auto;
}

.school-item {
  background-color: #fff;
  border-radius: 8px;
  padding: 15px;
  border: 1px solid #e0e0e0;
  transition: box-shadow 0.3s;
  cursor: pointer;
}

.school-item:hover {
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  border-color: #409EFF;
}

.school-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 10px;
}

.school-name {
  font-size: 16px;
  font-weight: bold;
  color: #333;
}

.school-tags {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.school-tags .tag {
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
}

.rank-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  padding: 2px 6px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  margin-right: 8px;
  box-shadow: 0 2px 4px rgba(102, 126, 234, 0.3);
}

.tag-985 {
  background-color: #f5222d;
  color: white;
}

.tag-211 {
  background-color: #fa8c16;
  color: white;
}

.tag-double {
  background-color: #52c41a;
  color: white;
}

.tag-type {
  background-color: #e6f7ff;
  color: #1890ff;
  border: 1px solid #91d5ff;
}

.school-info {
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: #666;
}

.icon {
  font-size: 14px;
  font-style: normal;
}

.location-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.empty-tip {
  text-align: center;
  padding: 40px;
  color: #999;
  font-size: 14px;
}

/* 分页样式 */
.pagination {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
  padding: 20px;
  background-color: #fff;
  border-top: 1px solid #e0e0e0;
  margin-top: 20px;
}

.pagination-info {
  font-size: 14px;
  color: #666;
}

.pagination-buttons {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: center;
}

.page-btn {
  padding: 6px 12px;
  border: 1px solid #dcdfe6;
  background-color: #fff;
  color: #606266;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s;
}

.page-btn:hover:not(:disabled) {
  border-color: #409EFF;
  color: #409EFF;
}

.page-btn.active {
  background-color: #409EFF;
  border-color: #409EFF;
  color: #fff;
}

.page-btn:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.page-size-selector {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #666;
}

.page-size-selector select {
  padding: 4px 8px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  font-size: 14px;
}

/* 访问限制提示样式 */
.access-limit-warning {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  padding: 40px;
}

.warning-content {
  text-align: center;
  background-color: #fdf6ec;
  border: 1px solid #f5dab1;
  border-radius: 8px;
  padding: 40px 60px;
  max-width: 500px;
}

.warning-icon {
  font-size: 48px;
  margin-bottom: 20px;
}

.warning-text h3 {
  color: #e6a23c;
  font-size: 20px;
  margin-bottom: 15px;
}

.warning-text p {
  color: #666;
  font-size: 14px;
  line-height: 1.6;
  margin-bottom: 10px;
}

.warning-text .limit-period {
  color: #409EFF;
  font-weight: bold;
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px dashed #dcdfe6;
}
</style>
